import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { FL } from "@/constants/testIds/fairylights";

const STORY_DURATION_MS = 4200;

export default function Highlights({ highlights }) {
  const [activeIdx, setActiveIdx] = useState(null);

  return (
    <section id="highlights" className="relative bg-[#4A0E1F] px-6 pt-16 sm:px-10 md:px-14">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="font-body text-[11px] uppercase tracking-[0.36em] text-[#D4AF37]">
              Highlights
            </span>
          </div>
          <span className="hidden font-italic-serif text-[15px] text-[#FDFBF7]/60 md:inline">
            tap a circle · watch a story
          </span>
        </div>

        <div
          data-testid={FL.highlightRow}
          className="no-scrollbar -mx-6 flex snap-x snap-mandatory gap-6 overflow-x-auto px-6 pb-6 sm:mx-0 sm:px-0"
        >
          {highlights.map((h, i) => (
            <motion.button
              key={h.id}
              data-testid={FL.highlight(h.id)}
              onClick={() => setActiveIdx(i)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4 }}
              className="group relative flex shrink-0 snap-start flex-col items-center gap-3"
            >
              <div className="gold-ring w-[92px] transition-transform duration-500 group-hover:scale-[1.03]">
                <div className="overflow-hidden rounded-full bg-[#1A050B]">
                  <img
                    src={h.cover}
                    alt={h.label}
                    className="aspect-square w-full object-cover"
                  />
                </div>
              </div>
              <span className="font-body text-[12px] uppercase tracking-[0.18em] text-[#FDFBF7]/75 transition-colors group-hover:text-[#D4AF37]">
                {h.label}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeIdx !== null && (
          <StoryViewer
            highlights={highlights}
            startIndex={activeIdx}
            onClose={() => setActiveIdx(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function StoryViewer({ highlights, startIndex, onClose }) {
  const [hIndex, setHIndex] = useState(startIndex);
  const [sIndex, setSIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const startedAt = useRef(Date.now());
  const rafRef = useRef(null);

  const current = highlights[hIndex];
  const story = current.stories[sIndex];

  useEffect(() => {
    startedAt.current = Date.now() - progress * STORY_DURATION_MS;
    function tick() {
      if (paused) {
        startedAt.current = Date.now() - progress * STORY_DURATION_MS;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const elapsed = Date.now() - startedAt.current;
      const p = Math.min(1, elapsed / STORY_DURATION_MS);
      setProgress(p);
      if (p >= 1) {
        advance(1);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hIndex, sIndex, paused]);

  function advance(dir) {
    setProgress(0);
    const nextS = sIndex + dir;
    if (nextS >= 0 && nextS < current.stories.length) {
      setSIndex(nextS);
      return;
    }
    const nextH = hIndex + dir;
    if (nextH < 0) {
      onClose();
      return;
    }
    if (nextH >= highlights.length) {
      onClose();
      return;
    }
    setHIndex(nextH);
    setSIndex(dir > 0 ? 0 : highlights[nextH].stories.length - 1);
  }

  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") advance(1);
      if (e.key === "ArrowLeft") advance(-1);
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hIndex, sIndex]);

  return (
    <motion.div
      data-testid={FL.storyModal}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#1A050B]/95 backdrop-blur-md"
    >
      <div className="relative w-full max-w-[420px] px-4">
        {/* Header */}
        <div className="mb-3 flex items-center gap-3">
          <div className="gold-ring w-[42px]">
            <div className="overflow-hidden rounded-full">
              <img src={current.cover} alt="" className="aspect-square w-full object-cover" />
            </div>
          </div>
          <div>
            <div className="font-body text-[13px] text-[#FDFBF7]">@fairylights.uae</div>
            <div className="font-body text-[11px] uppercase tracking-[0.24em] text-[#D4AF37]">
              {current.label}
            </div>
          </div>
          <button
            data-testid={FL.storyClose}
            onClick={onClose}
            className="ml-auto rounded-full p-2 text-[#FDFBF7]/80 hover:bg-white/10 hover:text-[#FDFBF7] transition-colors"
            aria-label="Close stories"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress bars */}
        <div className="mb-3 flex gap-1.5">
          {current.stories.map((_, i) => (
            <div key={i} className="story-progress">
              <span
                style={{
                  width:
                    i < sIndex
                      ? "100%"
                      : i === sIndex
                      ? `${progress * 100}%`
                      : "0%",
                }}
              />
            </div>
          ))}
        </div>

        {/* Story */}
        <motion.div
          key={`${hIndex}-${sIndex}`}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative aspect-[9/16] w-full overflow-hidden rounded-md bg-black"
          onMouseDown={() => setPaused(true)}
          onMouseUp={() => setPaused(false)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          <img
            src={story.image}
            alt={story.caption}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-5">
            <p className="font-italic-serif text-[18px] leading-[1.35] text-[#FDFBF7]">
              {story.caption}
            </p>
          </div>

          {/* Tap regions */}
          <button
            data-testid={FL.storyPrev}
            className="absolute inset-y-0 left-0 w-1/3"
            aria-label="Previous story"
            onClick={(e) => {
              e.stopPropagation();
              advance(-1);
            }}
          />
          <button
            data-testid={FL.storyNext}
            className="absolute inset-y-0 right-0 w-2/3"
            aria-label="Next story"
            onClick={(e) => {
              e.stopPropagation();
              advance(1);
            }}
          />
        </motion.div>
      </div>
    </motion.div>
  );
}
